<template>
  <div>
    <client-only>
      <Teleport to="#teleport-breadcrumb">
        <i18nLink
          v-if="parentRouteName && parentTitle"
          :to="{ name: parentRouteName }"
          :class="$style.link"
        >
          <LayoutTextWithBarComponent>{{ parentTitle }}</LayoutTextWithBarComponent>
        </i18nLink>

        <i18nLink
          v-if="routeName && titleTruncated"
          :to="{ name: routeName, params: { slug: slug, secondarySlug: secondarySlug }}"
          :class="$style.link"
        >
          <LayoutTextWithBarComponent>{{ titleTruncated }}</LayoutTextWithBarComponent>
        </i18nLink>
      </Teleport>
    </client-only>

    <client-only v-if="injectClose">
      <!-- NOTE should be prev route or parent -->
      <Teleport to="#teleport-link-to-close">
        <i18nLink
          :to="{ name: parentRouteName }"
          :class="$style.link"
        >
          <LayoutTextWithBarComponent>{{ $t('shared.close') }}</LayoutTextWithBarComponent>
        </i18nLink>
      </Teleport>
    </client-only>

    <h1 :class="$style.heading">
      <template v-if="$slots.default">
        <slot />
      </template>
      <template v-else>
        {{ title }}
      </template>
      <LayoutHorizontalLineComponent />
    </h1>
  </div>
</template>

<script setup>
const route = useRoute()
const { slug: slugFromRoute, secondaryslug: secondarySlugFromRoute } = route.params

const nuxtApp = useNuxtApp()
const routeName = computed(() => {
  return props?.routeName || nuxtApp.$getRouteBaseName()
})

const props = defineProps({
  pageData: Object,
  routeName: String,
  parentPageData: Object,
  parentRouteName: String,
  injectClose: {
    type: Boolean,
    default: true,
  }
})

const localizedData = computed(() => {
  return i18nGetLocalizedData(baseStrapiGetAttributes(props?.pageData))
})

const title = computed(() => {
  const title = (localizedData?.value?.titles?.current_locale || localizedData?.value?.titles?.i18n_string_en) || localizedData?.value?.title
  return title
})

const titleTruncated = computed(() => {
  return baseTruncateString(title.value, 4)
})

const slug = computed(() => {
  if (slugFromRoute && props?.routeName?.includes('secondaryslug')) {
    return slugFromRoute
  }
  return (localizedData?.value?.slugs?.current_locale || localizedData?.value?.slugs?.i18n_string_en) || localizedData?.value?.slug
})

const secondarySlug = computed(() => {
  if (slugFromRoute && props?.name?.includes('secondaryslug')) {
    return (localizedData?.value?.slugs?.current_locale || localizedData?.value?.slugs?.i18n_string_en) || localizedData?.value?.slug
  }

  return null
})

const localizedParentData = computed(() => {
  return i18nGetLocalizedData(baseStrapiGetAttributes(props?.parentPageData))
})

const parentTitle = computed(() => {
  return (localizedParentData?.value?.titles?.current_locale || localizedParentData?.value?.titles?.i18n_string_en)
})
</script>

<style module>
.heading {
  composes: reset-heading font-size-large container--medium from global;

  display: flex;
  flex-direction: column;
  gap: var(--unit--spacer);

  text-align: center;

  margin-bottom: calc(var(--unit--vertical) * 4);
}

.link {
  composes: reset-link from global;
}

.link:hover,
.link:global(.router-link-exact-active) {
  color: var(--color--accent);
}
</style>
